import {
  CollectForm_Base,
  CollectForm_Field,
} from '~/common/typescript/models/CollectForm';
import {
  FormField_V2,
  FormSchema_V2,
  SelectOption,
} from '~/common/typescript/models/CollectFormV2';

export interface ParsedFormField {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  options?: SelectOption[];
  config?: any;
}

export interface ParsedFormContent {
  fields: ParsedFormField[];
  version: 'v1' | 'v2';
  schema?: FormSchema_V2;
}

export interface TitleField {
  key: string;
  label: string;
}

/**
 * Parse CollectForm content - handles both v1 (array) and v2 (object) formats
 *
 * V1: content is array of CollectForm_Field directly
 *   Example: [{ key: 'name', label: 'Tên', type: 'text', required: true }]
 *
 * V2: content is FormSchema_V2 object with sections and fields
 *   Example: { fields: [{ id: 'name', label: 'Tên', type: 'text' }], settings: {...} }
 */
export const parseFormContent = (formData: CollectForm_Base | null): ParsedFormContent | null => {
  if (!formData) {
    return null;
  }

  try {
    let content = formData.content;

    // Parse if it's a string (JSON string)
    if (typeof content === 'string') {
      try {
        content = JSON.parse(content);
      } catch (e) {
        console.error('Failed to parse form content as JSON:', content);
        return null;
      }
    }

    // V2 Schema: form_version === 2 or content has 'fields' property as object
    if (formData.form_version === 2 || (typeof content === 'object' && 'fields' in content)) {
      const schema = content as unknown as FormSchema_V2;
      const fields: ParsedFormField[] = (schema.fields || []).map((field: FormField_V2) => ({
        key: field.id || '',
        label: field.label || '',
        type: field.type || 'text',
        required: typeof field.required === 'boolean' ? field.required : false,
        config: field.config,
        options: (field.config as any)?.options as SelectOption[] | undefined,
      }));

      return {
        fields,
        version: 'v2',
        schema,
      };
    }

    // V1 Schema: content is direct array of fields
    if (Array.isArray(content)) {
      const fields: ParsedFormField[] = content.map((field: CollectForm_Field) => ({
        key: field.key || '',
        label: field.label || '',
        type: field.type || 'text',
        required: field.required,
        options: field.options?.map((opt) => ({
          value: opt,
          label: opt,
        })) as SelectOption[] | undefined,
      }));

      return {
        fields,
        version: 'v1',
      };
    }

    console.warn('Unknown form content format:', content);
    return null;
  } catch (error) {
    console.error('Error parsing form content:', error);
    return null;
  }
};


/**
 * Get text fields from form content for filtering/searching
 */
export const getTextFieldsFromContent = (formData: CollectForm_Base | null): TitleField[] => {
  const parsed = parseFormContent(formData);
  if (!parsed) return [];

  return parsed.fields
    .filter((field) => {
      const type = field.type?.toLowerCase();
      return type === 'text' || type === 'textarea' || type === 'select' || !type;
    })
    .slice(0, 3) // Limit to 3 fields
    .map((field) => ({
      key: field.key,
      label: field.label || field.key,
    }));
};

/**
 * Find the title field from form content
 */
export const findTitleField = (formData: CollectForm_Base | null): TitleField | null => {
  const parsed = parseFormContent(formData);
  if (!parsed) return null;

  const titleFields = ['name', 'title', 'ten', 'label', 'description', 'mo_ta'];

  // Try to find a field that matches title patterns
  const titleField = parsed.fields.find((field) =>
    titleFields.some((tf) => field.key?.toLowerCase().includes(tf)),
  );

  if (titleField) {
    return { key: titleField.key, label: titleField.label || titleField.key };
  }

  // Fall back to first text field
  const textField = parsed.fields.find(
    (field) => field.type === 'text' || field.type === 'textarea' || !field.type,
  );

  if (textField) {
    return { key: textField.key, label: textField.label || textField.key };
  }

  // Fall back to first field
  if (parsed.fields.length > 0) {
    return {
      key: parsed.fields[0].key,
      label: parsed.fields[0].label || parsed.fields[0].key,
    };
  }

  return null;
};